/* SPDX-FileCopyrightText: Copyright (c) 2026 NVIDIA CORPORATION & AFFILIATES. All rights reserved. */
/* SPDX-License-Identifier: Apache-2.0 */

:host {
  display: flex;
  width: 100%;
  line-height: 0;
  --height: var(--nve-ref-size-200);
  --opacity: 1;
  --accent-color: var(--nve-sys-interaction-color);
  --background: var(--nve-sys-interaction-background);
}

:host([status='accent']) {
  --accent-color: var(--nve-sys-accent-secondary-background);
}

:host([status='success']) {
  --accent-color: var(--nve-sys-support-success-color);
}

:host([status='warning']) {
  --accent-color: var(--nve-sys-support-warning-color);
}

:host([status='danger']) {
  --accent-color: var(--nve-sys-support-danger-color);
}

progress {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background: var(--background);
  border-radius: var(--nve-ref-border-radius-full);
  height: var(--height);
  opacity: var(--opacity);
  width: 100%;
}

progress[value]::-webkit-progress-bar {
  background-color: inherit;
  border-radius: var(--nve-ref-border-radius-full);
}

progress[value]::-webkit-progress-value {
  border-radius: var(--nve-ref-border-radius-full);
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  background-color: var(--accent-color);
}

progress[value].full::-webkit-progress-value {
  border-top-right-radius: var(--nve-ref-border-radius-full);
  border-bottom-right-radius: var(--nve-ref-border-radius-full);
}

progress[value].min-width::-webkit-progress-value {
  min-width: 5px;
}

progress[value]::-moz-progress-bar {
  border-radius: var(--nve-ref-border-radius-full);
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  background-color: var(--accent-color);
}

:host(:not([value])) {
  --height: var(--nve-ref-size-100);
  --opacity: 0.75;
}

progress.indeterminate {
  --accent-color-faded: color-mix(in srgb, var(--accent-color), transparent 90%);
  --indeterminate-gradient: linear-gradient(to right, var(--accent-color-faded) 10%, var(--accent-color));
  --indeterminate-gradient-reverse: linear-gradient(to left, var(--accent-color-faded) 10%, var(--accent-color));
  will-change: transform;
  background-image: var(--indeterminate-gradient);
  animation: indeterminate-animation 2.4s infinite cubic-bezier(0.645, 0.045, 0.355, 1);
}

progress[value].indeterminate::-webkit-progress-value {
  border-radius: var(--nve-ref-border-radius-none);
  background-color: transparent !important;
}

@keyframes indeterminate-animation {
  0% {
    transform: scaleX(0.015);
    transform-origin: 0% 0%;
  }

  25% {
    transform: scaleX(0.4);
  }

  50% {
    transform: scaleX(0.015);
    transform-origin: 100% 0%;
    background-image: var(--indeterminate-gradient);
  }

  50.1% {
    transform: scaleX(0.015);
    transform-origin: 100% 0%;
    background-image: var(--indeterminate-gradient-reverse);
  }

  75% {
    transform: scaleX(0.4);
  }

  100% {
    transform: scaleX(0.015);
    transform-origin: 0% 0%;
    background-image: var(--indeterminate-gradient-reverse);
  }
}
